import { Suspense, lazy, useState } from "react";
import {
  Alert,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useParams } from "react-router";
import {
  ApiError,
  useBlockHead,
  useDomainDetail,
  useDomainMiningEfficiency,
  useDomainMiningSeries,
  useDomainSentencesPage,
  type DomainSentenceEntry,
} from "../api";
import { shortenHex } from "../identity";
import { formatCog } from "../units";
import { QueryCard } from "../components/QueryCard";
import { OutcomeBadge } from "../components/OutcomeBadge";
import { EfficiencyGrid } from "../components/EfficiencyGrid";
import { PaginationControls } from "../components/PaginationControls";
import { usePaginationCursor } from "../hooks/usePaginationCursor";
import { SENTENCES_PAGE_SIZE } from "../constants";

const MiningSeriesChart = lazy(() =>
  import("../charts/MiningSeriesChart").then((m) => ({ default: m.MiningSeriesChart })),
);
const CumulativeRewardsChart = lazy(() =>
  import("../charts/CumulativeRewardsChart").then((m) => ({
    default: m.CumulativeRewardsChart,
  })),
);
const RewardRateChart = lazy(() =>
  import("../charts/RewardRateChart").then((m) => ({
    default: m.RewardRateChart,
  })),
);

const REWARD_RATE_WINDOWS = [
  { value: "24", label: "24 blocks" },
  { value: "144", label: "144 blocks" },
  { value: "500", label: "500 blocks" },
  { value: "1008", label: "1008 blocks" },
] as const;
const DEFAULT_REWARD_RATE_WINDOW = 144;

export function PerDomain() {
  const { domainId: rawDomainId } = useParams();
  const parsedDomainId = parseDomainIdParam(rawDomainId);

  if (parsedDomainId === null) {
    return (
      <Stack gap="md">
        <Title order={2}>Domain</Title>
        <Alert color="red" title="Invalid domain id">
          The URL must look like <code>/domain/&lt;positive-integer&gt;</code>.
        </Alert>
      </Stack>
    );
  }

  return <PerDomainView domainId={parsedDomainId} />;
}

interface PerDomainViewProps {
  readonly domainId: number;
}

function PerDomainView({ domainId }: PerDomainViewProps) {
  const head = useBlockHead();
  const toHeight = head.data ? Number(head.data.height) : null;

  const detail = useDomainDetail(domainId, toHeight);
  const series = useDomainMiningSeries(domainId, toHeight);
  const efficiency = useDomainMiningEfficiency(domainId, toHeight);

  const [rewardRateWindow, setRewardRateWindow] = useState<number>(
    DEFAULT_REWARD_RATE_WINDOW,
  );

  const pagination = usePaginationCursor();
  const sentences = useDomainSentencesPage(domainId, toHeight, {
    cursor: pagination.cursor,
    limit: SENTENCES_PAGE_SIZE,
  });

  const detailName = detail.data?.name ?? null;
  const detailMissing =
    detail.error instanceof ApiError && detail.error.status === 404;

  return (
    <Stack gap="lg">
      <Group gap="xs" align="baseline">
        <Title order={2}>Domain</Title>
        {detailName !== null ? (
          <Text size="lg" fw={500}>
            {detailName}
          </Text>
        ) : detailMissing ? (
          <Text size="lg" c="dimmed">
            (unregistered at this height)
          </Text>
        ) : detail.isError ? (
          <Text size="lg" c="dimmed">
            (name unavailable)
          </Text>
        ) : null}
        <Text size="lg" c="dimmed">
          #{domainId}
        </Text>
      </Group>

      <QueryCard title="Mining activity" query={series} alsoWatch={[head]}>
        {(data) =>
          data.entries.length > 0 ? (
            <Suspense fallback={<Loader size="sm" />}>
              <MiningSeriesChart entries={data.entries} />
            </Suspense>
          ) : (
            <Text c="dimmed">No submissions yet.</Text>
          )
        }
      </QueryCard>

      <QueryCard title="Mining efficiency" query={efficiency}>
        {(data) => <EfficiencyGrid totals={data} />}
      </QueryCard>

      <QueryCard title="Cumulative rewards earned" query={series} alsoWatch={[head]}>
        {(data) =>
          data.entries.some((e) => e.rewardCogtoshi > 0n) ? (
            <Suspense fallback={<Loader size="sm" />}>
              <CumulativeRewardsChart entries={data.entries} />
            </Suspense>
          ) : (
            <Text c="dimmed">No rewards yet.</Text>
          )
        }
      </QueryCard>

      <QueryCard
        title={
          <Group justify="space-between" align="center">
            <Title order={4}>
              Reward rate ({rewardRateWindow}-block moving average)
            </Title>
            <Select
              size="xs"
              data={[...REWARD_RATE_WINDOWS]}
              value={rewardRateWindow.toString()}
              onChange={(v) => {
                if (v !== null) setRewardRateWindow(Number(v));
              }}
              allowDeselect={false}
              aria-label="Moving average window"
            />
          </Group>
        }
        query={series}
        alsoWatch={[head]}
      >
        {(data) =>
          data.entries.some((e) => e.rewardCogtoshi > 0n) ? (
            <Suspense fallback={<Loader size="sm" />}>
              <RewardRateChart
                entries={data.entries}
                windowBlocks={rewardRateWindow}
              />
            </Suspense>
          ) : (
            <Text c="dimmed">No rewards yet.</Text>
          )
        }
      </QueryCard>

      <QueryCard
        title={
          <Group justify="space-between" align="baseline">
            <Title order={4}>Submitted sentences</Title>
            <PaginationControls
              pagination={pagination}
              isFetching={sentences.isFetching}
              nextCursor={sentences.data?.nextCursor ?? null}
            />
          </Group>
        }
        query={sentences}
      >
        {(data) => <SentencesTable rows={data.entries} />}
      </QueryCard>
    </Stack>
  );
}

function parseDomainIdParam(s: string | undefined): number | null {
  if (s === undefined) return null;
  if (!/^[1-9][0-9]*$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) ? n : null;
}

interface SentencesTableProps {
  readonly rows: readonly DomainSentenceEntry[];
}

function SentencesTable({ rows }: SentencesTableProps) {
  if (rows.length === 0) {
    return <Text c="dimmed">No sentences in this window.</Text>;
  }
  return (
    <Table withTableBorder striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Height</Table.Th>
          <Table.Th>Outcome</Table.Th>
          <Table.Th>Sentence</Table.Th>
          <Table.Th>Blend</Table.Th>
          <Table.Th>Reward</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => (
          <Table.Tr key={`${row.txid}:${row.txIndex.toString()}`}>
            <Table.Td>{row.height.toString()}</Table.Td>
            <Table.Td>
              <OutcomeBadge entry={row} />
            </Table.Td>
            <Table.Td>
              {row.sentenceText !== null ? (
                <Text size="sm">{row.sentenceText}</Text>
              ) : (
                <Text size="xs" c="dimmed" ff="monospace">
                  {shortenHex(row.rawSentenceBytes, 12, 6)}
                </Text>
              )}
            </Table.Td>
            <Table.Td>
              {row.canonicalBlend !== null ? row.canonicalBlend.toString() : "—"}
            </Table.Td>
            <Table.Td>
              {row.rewardCogtoshi > 0n
                ? formatCog(row.rewardCogtoshi, {
                    groupIntegerDigits: true,
                    minDecimals: 2,
                  })
                : "—"}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

